"use client";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MicIcon from "@mui/icons-material/Mic";
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import type { DayOfWeek } from "@prisma/client";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import type { FormEvent } from "react";
import { useRef, useState } from "react";

import { CustomDatePicker } from "@/components/CustomDatePicker";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { type TurnstileHandle, TurnstileWidget, useTurnstile } from "@/hooks/useTurnstile";
import { useTypewriter } from "@/hooks/useTypewriter";
import type { BookingArea } from "@/server-fns/booking/bookingAreas";
import type { BookingTime } from "@/server-fns/booking/query";
import { extractBookingIntentFn, getTimesByDate } from "@/server-fns/booking/serverFns";
import type { BookingIntent } from "@/types/booking-intent";
import type { DateYYYYMMDD } from "@/types/date";
import { extractLocalBookingIntent } from "@/utils/booking-heuristics-client";
import { totalGuestsFromForm } from "@/utils/partySize";
import { storeNow, storeToday } from "@/utils/storeTime";

import { AISuggestions, type TimeSuggestion } from "./AISuggestions";

const EXAMPLES = [
    "4 adults, Saturday evening, window side please",
    "Birthday dinner for 6 next Friday at 7pm",
    "明天晚上7点，两位，靠窗的位置",
    "Une table pour deux demain à 19 h",
];

function AISparkleLoader({ size = 20 }: { size?: number }) {
    const theme = useTheme();
    const stars = [
        "M19 9 l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25z",
        "M11.5 9.5 L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12z",
        "M19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25z",
    ];
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill={theme.palette.primary.main}>
            {stars.map((d, i) => (
                <motion.path
                    key={i}
                    d={d}
                    animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 1, 0.3] }}
                    transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        delay: i * 0.35,
                        ease: "easeInOut",
                    }}
                />
            ))}
        </svg>
    );
}

type Props = {
    storeId: string;
    storeTimezone: string;
    onBookingIntent: (intent: BookingIntent) => void;
    onManualMode: (hasInput: boolean) => void;
};

export const AICommandBar = ({ storeId, storeTimezone, onBookingIntent, onManualMode }: Props) => {
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [suggestions, setSuggestions] = useState<TimeSuggestion[]>([]);
    const [suggestionTitle, setSuggestionTitle] = useState<string | undefined>(undefined);
    const [extractedIntent, setExtractedIntent] = useState<BookingIntent | null>(null);
    const [pendingIntent, setPendingIntent] = useState<BookingIntent | null>(null);
    const [followUpDate, setFollowUpDate] = useState<Dayjs | null>(null);
    const [followUpAdults, setFollowUpAdults] = useState("");
    const { captchaToken, setCaptchaToken } = useTurnstile();
    const turnstileRef = useRef<TurnstileHandle>(null);
    const { isListening, startListening, stopListening, hasRecognition } = useSpeechRecognition();

    const queryClient = useQueryClient();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    // Typewriter effect
    const placeholder = useTypewriter(EXAMPLES);

    const getNearbyOptions = (requestedTime: string, availabilityData: BookingTime[]) => {
        const [reqHour, reqMin] = requestedTime.split(":").map(Number);
        const reqTotalMin = reqHour * 60 + reqMin;

        return availabilityData
            .filter((t) => t.hasCapacity)
            .map((t) => {
                const [h, m] = t.bookingStartTimeHHMM.split(":").map(Number);
                const totalMin = h * 60 + m;
                const diff = Math.abs(totalMin - reqTotalMin);
                return { ...t, diff };
            })
            .filter((t) => t.diff <= 120) // Only within 2 hours
            .sort((a, b) => a.bookingStartTimeHHMM.localeCompare(b.bookingStartTimeHHMM));
    };

    const getPeriodOptions = (
        period: "lunch" | "evening" | null | undefined,
        availabilityData: BookingTime[],
    ) => {
        let filtered = availabilityData.filter((t) => t.hasCapacity);

        if (period === "evening") {
            filtered = filtered.filter((t) => {
                const hour = parseInt(t.bookingStartTimeHHMM.split(":")[0]);
                return hour >= 17;
            });
        } else if (period === "lunch") {
            filtered = filtered.filter((t) => {
                const hour = parseInt(t.bookingStartTimeHHMM.split(":")[0]);
                return hour >= 11 && hour < 17;
            });
        }

        return filtered.sort((a, b) =>
            a.bookingStartTimeHHMM.localeCompare(b.bookingStartTimeHHMM),
        );
    };

    const checkAvailabilityAndShow = async (intent: BookingIntent) => {
        const availabilityResult = await queryClient.fetchQuery({
            queryKey: [
                "bookingTimes",
                storeId,
                intent.date,
                dayjs(intent.date).format("dddd"),
                totalGuestsFromForm(intent),
            ],
            queryFn: () =>
                getTimesByDate({
                    data: {
                        storeId,
                        date: intent.date as DateYYYYMMDD,
                        dayOfWeek: dayjs(intent.date).format("dddd") as DayOfWeek,
                        totalGuests: totalGuestsFromForm(intent),
                    },
                }),
        });
        const { areas } = availabilityResult;
        const hasAreaChoice = areas.length >= 2;
        // With areas, a slot is open only when some single area can seat the party: hasCapacity
        // can also be true through a connected-table combination that spans areas, and area
        // booking never offers those.
        const availabilityData = hasAreaChoice
            ? availabilityResult.bookingTimes.map((t) => ({
                  ...t,
                  hasCapacity: t.availableAreaIds.length > 0,
              }))
            : availabilityResult.bookingTimes;
        // One suggestion per (time, area) the party fits in, so each chip names where they'd sit.
        const toSuggestions = (times: BookingTime[]): TimeSuggestion[] =>
            hasAreaChoice
                ? times.flatMap((t) =>
                      areas
                          .filter((area) => t.availableAreaIds.includes(area.id))
                          .map((area) => ({ bookingStartTimeHHMM: t.bookingStartTimeHHMM, area })),
                  )
                : times;

        if (intent.time) {
            const exactMatch = availabilityData.find(
                (t) => t.bookingStartTimeHHMM === intent.time && t.hasCapacity,
            );
            if (exactMatch) {
                if (!hasAreaChoice) {
                    onBookingIntent(intent);
                    return;
                }
                const openAreas = areas.filter((area) =>
                    exactMatch.availableAreaIds.includes(area.id),
                );
                if (openAreas.length === 1) {
                    onBookingIntent({
                        ...intent,
                        areaId: openAreas[0].id,
                        areaName: openAreas[0].name,
                    });
                    return;
                }
                setSuggestionTitle(`Which seating area would you like at ${intent.time}?`);
                setSuggestions(
                    openAreas.map((area) => ({
                        bookingStartTimeHHMM: exactMatch.bookingStartTimeHHMM,
                        area,
                    })),
                );
                return;
            }
            const nearby = getNearbyOptions(intent.time, availabilityData);
            if (nearby.length > 0) {
                setSuggestionTitle("Nearby availability found! How about these?");
                setSuggestions(toSuggestions(nearby));
            } else {
                const allAvailable = availabilityData
                    .filter((t) => t.hasCapacity)
                    .sort((a, b) => a.bookingStartTimeHHMM.localeCompare(b.bookingStartTimeHHMM));
                if (allAvailable.length > 0) {
                    setSuggestionTitle(
                        `${intent.time} isn't available. Here are all open slots for ${dayjs(intent.date).format("ddd, MMM D")}:`,
                    );
                    setSuggestions(toSuggestions(allAvailable));
                } else {
                    setError(
                        "No times available for this date. Try another date or check manually.",
                    );
                }
            }
            return;
        }

        const periodOptions = getPeriodOptions(intent.period, availabilityData);
        if (periodOptions.length > 0) {
            setSuggestionTitle(intent.periodTitle || "Available slots for your request");
            setSuggestions(toSuggestions(periodOptions));
        } else {
            const allAvailable = availabilityData
                .filter((t) => t.hasCapacity)
                .sort((a, b) => a.bookingStartTimeHHMM.localeCompare(b.bookingStartTimeHHMM));
            if (allAvailable.length > 0) {
                const periodLabel =
                    intent.period === "evening"
                        ? "evening"
                        : intent.period === "lunch"
                          ? "lunch"
                          : null;
                setSuggestionTitle(
                    periodLabel
                        ? `No ${periodLabel} slots available. Here are all open times for ${dayjs(intent.date).format("ddd, MMM D")}:`
                        : `Here are all available times for ${dayjs(intent.date).format("ddd, MMM D")}:`,
                );
                setSuggestions(toSuggestions(allAvailable));
            } else {
                setError("No times available for this date. Try another date or check manually.");
            }
        }
    };

    const handleMicClick = () => {
        if (isListening) {
            stopListening();
        } else {
            const currentInput = input;
            startListening(
                (transcript) => {
                    const prefix = currentInput.trim() ? currentInput.trim() + " " : "";
                    setInput(prefix + transcript);
                },
                (err) => {
                    if (err === "not-allowed") {
                        setError(
                            "Microphone access was denied. Please allow microphone access in your browser settings and try again.",
                        );
                    }
                },
            );
        }
    };

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        if (isListening) stopListening();
        if (!input.trim()) return;

        setIsLoading(true);
        setError(null);
        setSuggestions([]);
        try {
            // 스토어 타임존 기준 "오늘" — 게스트의 브라우저 타임존이 스토어와 다르면
            // "tomorrow" 같은 상대 날짜 표현이 엉뚱한 날짜로 해석된다.
            const today = storeToday(storeTimezone);

            // 1. Try local heuristics first (Client-side, free)
            let result;
            const localIntent = extractLocalBookingIntent(input, storeTimezone);
            if (localIntent) {
                // Optimization: If local parser figured it out, use it directly!
                result = { success: true, data: localIntent };
            } else {
                // 2. Fallback to AI (Server-side, cost = gpt-4o-mini)
                console.info("Hitting AI");
                result = await extractBookingIntentFn({
                    data: { text: input, today, token: captchaToken },
                });
                turnstileRef.current?.reset();
            }
            if (!result.success || !result.data) {
                setError(result.message || "I couldn't quite catch that. Try again?");
                return;
            }

            const intent = result.data;
            setExtractedIntent(intent);

            // 3. Basic check: Do we have enough info to check availability?
            if (!intent.date || !intent.adults) {
                setPendingIntent(intent);
                return;
            }

            // 4-6. Fetch availability and show results
            await checkAvailabilityAndShow(intent);
        } catch (err) {
            Sentry.captureException(err);
            console.error("AI Booking submit error:", err);
            setError("Something went wrong. Please try manual booking.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleFollowUpSubmit = async () => {
        if (!pendingIntent) return;
        const mergedDate = followUpDate ? followUpDate.format("YYYY-MM-DD") : pendingIntent.date;
        const mergedAdults = followUpAdults ? parseInt(followUpAdults) : pendingIntent.adults;
        if (!mergedDate || !mergedAdults) return;

        const mergedIntent = { ...pendingIntent, date: mergedDate, adults: mergedAdults };
        setExtractedIntent(mergedIntent);
        setPendingIntent(null);
        setFollowUpDate(null);
        setFollowUpAdults("");

        setIsLoading(true);
        setError(null);
        setSuggestions([]);
        try {
            await checkAvailabilityAndShow(mergedIntent);
        } catch (err) {
            Sentry.captureException(err);
            setError("Something went wrong. Please try manual booking.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSuggestionSelect = (time: string, area?: BookingArea) => {
        if (extractedIntent) {
            onBookingIntent({
                ...extractedIntent,
                time,
                ...(area ? { areaId: area.id, areaName: area.name } : {}),
            });
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                textAlign: "center",
                px: { xs: 0, md: 1 },
            }}
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                style={{ width: "100%" }}
            >
                <Typography
                    variant="h5"
                    fontWeight="700"
                    sx={{
                        mb: 1.5,
                        fontSize: { xs: "1.1rem", md: "1.25rem" },
                        letterSpacing: "-0.01em",
                        color: "text.primary",
                    }}
                >
                    Let&apos;s book your table
                </Typography>
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "600px",
                        mx: "auto",
                        borderRadius: "100px",
                        border: "1.5px solid transparent",
                        background:
                            "linear-gradient(white, white) padding-box, linear-gradient(135deg, #ea580c, #f97316, #fb923c) border-box",
                        boxShadow: "0 0 18px rgba(249, 115, 22, 0.18)",
                        transition: "all 0.3s ease",
                        "&:focus-within": {
                            boxShadow:
                                "0 0 32px rgba(249, 115, 22, 0.45), 0 0 64px rgba(234, 88, 12, 0.12)",
                        },
                    }}
                >
                    <TurnstileWidget ref={turnstileRef} onTokenChange={setCaptchaToken} />
                    <TextField
                        fullWidth
                        multiline={isSmallScreen}
                        variant="standard"
                        placeholder={`e.g., ${placeholder}`}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        disabled={isLoading}
                        InputProps={{
                            disableUnderline: true,
                            sx: {
                                py: 1.5,
                                px: 1,
                                fontSize: "1rem",
                            },
                            startAdornment: (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 32,
                                        height: 32,
                                        ml: 1.5,
                                        mr: 1.5,
                                    }}
                                >
                                    <AutoAwesomeIcon sx={{ color: "primary.main", fontSize: 28 }} />
                                </Box>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    {hasRecognition && (
                                        <IconButton
                                            onClick={handleMicClick}
                                            disabled={isLoading}
                                            sx={{
                                                color: isListening
                                                    ? "error.main"
                                                    : "text.secondary",
                                                mr: 1,
                                                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                                            }}
                                        >
                                            {isListening ? (
                                                <motion.div
                                                    animate={{ scale: [1, 1.2, 1] }}
                                                    transition={{ repeat: Infinity, duration: 1.5 }}
                                                >
                                                    <MicIcon />
                                                </motion.div>
                                            ) : (
                                                <MicIcon />
                                            )}
                                        </IconButton>
                                    )}
                                    <IconButton
                                        type="submit"
                                        disabled={!input.trim() || isLoading}
                                        sx={{
                                            bgcolor: "#F3F4F6",
                                            "&:hover": {
                                                bgcolor: "#E5E7EB",
                                            },
                                            p: 1,
                                        }}
                                    >
                                        {isLoading ? (
                                            <AISparkleLoader size={20} />
                                        ) : (
                                            <ArrowForwardIcon sx={{ fontSize: 20 }} />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <AnimatePresence>
                    {pendingIntent && (
                        <motion.div
                            key="follow-up"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                        >
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 2,
                                    borderRadius: 3,
                                    bgcolor: "grey.50",
                                    maxWidth: "600px",
                                    mx: "auto",
                                    textAlign: "left",
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    {!pendingIntent.date && !pendingIntent.adults
                                        ? "Just a couple more details:"
                                        : !pendingIntent.date
                                          ? "What date were you thinking?"
                                          : "How many guests?"}
                                </Typography>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    {!pendingIntent.date && (
                                        <Box sx={{ flex: 1 }}>
                                            <CustomDatePicker
                                                label="Date"
                                                value={followUpDate}
                                                onChange={setFollowUpDate}
                                                minDate={storeNow(storeTimezone)}
                                            />
                                        </Box>
                                    )}
                                    {!pendingIntent.adults && (
                                        <TextField
                                            type="number"
                                            label="Guests"
                                            value={followUpAdults}
                                            onChange={(e) => setFollowUpAdults(e.target.value)}
                                            inputProps={{ min: 1, max: 20 }}
                                            sx={{ flex: 1 }}
                                        />
                                    )}
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleFollowUpSubmit}
                                        disabled={
                                            isLoading ||
                                            (!pendingIntent.date && !followUpDate) ||
                                            (!pendingIntent.adults && !followUpAdults)
                                        }
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: 2,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {isLoading ? (
                                            <AISparkleLoader size={16} />
                                        ) : (
                                            "Check availability"
                                        )}
                                    </Button>
                                </Stack>
                            </Box>
                        </motion.div>
                    )}
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                        >
                            <Box sx={{ mt: 2 }}>
                                <Typography color="error" sx={{ mb: 1.5 }}>
                                    {error}
                                </Typography>
                                {error.includes("manually") && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        onClick={() => onManualMode(true)}
                                        sx={{ borderRadius: "20px", textTransform: "none" }}
                                    >
                                        Check manually
                                    </Button>
                                )}
                            </Box>
                        </motion.div>
                    )}
                    {suggestions.length > 0 && (
                        <AISuggestions
                            suggestions={suggestions}
                            title={suggestionTitle}
                            onSelect={handleSuggestionSelect}
                            onManualMode={onManualMode}
                        />
                    )}
                </AnimatePresence>

                <Box sx={{ mt: 3, maxWidth: "600px", mx: "auto", width: "100%" }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => onManualMode(input.trim().length > 0)}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            px: 2,
                            py: 0.6,
                            fontSize: "0.8rem",
                        }}
                    >
                        See all available times
                    </Button>
                </Box>
            </motion.div>
        </Box>
    );
};
